import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Shield, CheckCircle } from 'lucide-react';
import { useTheme } from '../contexts/Themecontext';
import { authAPI } from '../services/api';

const AdminRequestAccess = () => {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState(1); // 1: Request OTP, 2: Verify OTP, 3: Success
  const [signupLink, setSignupLink] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { theme } = useTheme();

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
        const response = await authAPI.adminRequestSignup(email);
        // Show success notification if your API returns a message
        console.log(response.message);
        setStep(2);
    } catch (err) {
        // Capture the detailed error message from the backend
        const backendError = err.response?.data?.error || 'Connection Failed';
        setError(`Server Error: ${backendError}`);
    } finally {
        setLoading(false);
    }
};
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authAPI.adminVerifyOTP(email, otp);
      setSignupLink(response.signup_link);
      setStep(3);
    } catch (err) {
      console.error('Verify OTP error:', err);
      setError(err.response?.data?.error || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getThemeClasses = () => {
    if (theme === 'light') {
      return {
        bg: 'bg-gray-50',
        card: 'bg-white',
        border: 'border-gray-200',
        text: 'text-gray-900',
        textMuted: 'text-gray-600',
        input: 'bg-white border-gray-300 text-gray-900 focus:border-purple-500',
        button: 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500',
      };
    }
    if (theme === 'dark') {
      return {
        bg: 'bg-slate-900',
        card: 'bg-slate-800',
        border: 'border-slate-700',
        text: 'text-white',
        textMuted: 'text-gray-400',
        input: 'bg-slate-900 border-slate-700 text-white focus:border-purple-500',
        button: 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500',
      };
    }
    return {
      bg: 'bg-gradient-to-br from-slate-950 via-purple-950 to-pink-950',
      card: 'bg-slate-900/50 backdrop-blur-xl',
      border: 'border-purple-500/30',
      text: 'text-white',
      textMuted: 'text-gray-300',
      input: 'bg-slate-900/50 border-purple-500/30 text-white focus:border-purple-400',
      button: 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500',
    };
  };

  const classes = getThemeClasses();

  return (
    <div className={`min-h-screen ${classes.bg} flex items-center justify-center px-4 py-12 pt-20 sm:px-6 lg:px-8`}>
      <div className="relative max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full overflow-hidden flex items-center justify-center bg-gradient-to-r from-purple-600 to-pink-600 p-1">
              <div className="w-full h-full bg-slate-900 rounded-full flex items-center justify-center">
                <Shield className="w-8 h-8 text-purple-400" />
              </div>
            </div>
          </div>
          <h2 className={`text-3xl font-bold ${classes.text}`}>Request Admin Access</h2>
          <p className={`mt-2 ${classes.textMuted}`}>
            {step === 1 && 'Enter your email to receive an OTP'}
            {step === 2 && 'Check your email for the verification code'}
            {step === 3 && 'Access granted! Check your email for the signup link'}
          </p>
        </div>

        <div className={`${classes.card} border ${classes.border} rounded-2xl shadow-2xl p-8`}>
          {error && (
            <div className="mb-4 p-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-500 text-sm">
              {error}
            </div>
          )}

          {/* Step 1: Request OTP */}
          {step === 1 && (
            <form onSubmit={handleRequestOTP} className="space-y-6">
              <div>
                <label className={`block text-sm font-medium ${classes.text} mb-2`}>
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className={`w-5 h-5 ${classes.textMuted}`} />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className={`block w-full pl-10 pr-3 py-3 border ${classes.input} rounded-lg focus:outline-none focus:ring-2 transition-colors`}
                    placeholder="your.email@glyqix.com"
                  />
                </div>
                <p className={`mt-2 text-xs ${classes.textMuted}`}>
                  An OTP will be sent to the admin email address
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full flex items-center justify-center gap-2 px-4 py-3 text-white font-semibold rounded-lg ${classes.button} shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Sending OTP...
                  </>
                ) : (
                  'Send Verification Code'
                )}
              </button>
            </form>
          )}

          {/* Step 2: Verify OTP */}
          {step === 2 && (
            <form onSubmit={handleVerifyOTP} className="space-y-6">
              <div>
                <label className={`block text-sm font-medium ${classes.text} mb-2`}>
                  Verification Code
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Shield className={`w-5 h-5 ${classes.textMuted}`} />
                  </div>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    required
                    maxLength={6}
                    className={`block w-full pl-10 pr-3 py-3 border ${classes.input} rounded-lg focus:outline-none focus:ring-2 transition-colors text-center text-2xl tracking-widest`}
                    placeholder="000000"
                  />
                </div>
                <p className={`mt-2 text-xs ${classes.textMuted}`}>
                  Enter the 6-digit code sent to the admin email
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full flex items-center justify-center gap-2 px-4 py-3 text-white font-semibold rounded-lg ${classes.button} shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify Code'
                )}
              </button>

              <button
                type="button"
                onClick={() => setStep(1)}
                className="w-full text-center text-sm text-gray-500 hover:text-gray-700"
              >
                ← Back
              </button>
            </form>
          )}

          {/* Step 3: Success */}
          {step === 3 && (
            <div className="text-center space-y-6">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
              <div>
                <h3 className={`text-xl font-bold ${classes.text} mb-2`}>
                  Verification Successful!
                </h3>
                <p className={`${classes.textMuted} mb-4`}>
                  A signup link has been sent to your email address.
                </p>
                {signupLink && (
                  <div className={`p-4 ${theme === 'light' ? 'bg-gray-100' : 'bg-slate-900'} rounded-lg mb-4`}>
                    <p className={`text-xs ${classes.textMuted} mb-2`}>
                      Or use this direct link:
                    </p>
                    <a
                      href={signupLink}
                      className="text-purple-400 hover:text-purple-300 text-sm break-all"
                    >
                      {signupLink}
                    </a>
                  </div>
                )}
              </div>
              <Link
                to="/admin/login"
                className={`inline-block px-6 py-3 rounded-lg font-semibold bg-gradient-to-r ${classes.button.replace('hover:', '')} text-white shadow-lg transition-all`}
              >
                Go to Admin Login
              </Link>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link to="/admin/login" className={`text-sm ${classes.textMuted} hover:text-purple-400 transition-colors`}>
              Already have access? Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminRequestAccess;